import React, { useCallback, useMemo, useState, useRef } from 'react';
import { debounce as _debounce } from 'lodash';
import { useTheme } from '@contexts/ThemeContext';
import JarvisTextInput from './JarvisTextInput';
import JarvisTextarea from './JarvisTextarea';

// Create a properly typed debounce function
const createDebouncedFunction = <T extends (...args: any[]) => any>(func: T, wait: number) => {
  return _debounce(func, wait);
};

// Helper function to check if we're in tab switching mode
const isTabSwitching = () => {
  return window.__IS_TAB_SWITCHING__ || document.hidden;
};

// Jarvis-themed debounced text input
export const JarvisDebouncedTextInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  styles?: any;
  uppercase?: boolean;
  name?: string;
  size?: string;
  numericOnly?: boolean;
  maxLength?: number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  pattern?: string;
}> = React.memo(({
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  required = false,
  disabled = false,
  styles = {},
  uppercase = false,
  name,
  size = "sm",
  numericOnly = false,
  maxLength,
  inputMode,
  pattern
}) => {
  const { colors: themeColors } = useTheme();

  // Local state to track input value
  const [localValue, setLocalValue] = useState(value || "");

  // Use refs to track the latest values without causing re-renders
  const lastPropValueRef = useRef(value);
  const isFocusedRef = useRef(false);
  // Ref for onChange so the debounced function never needs to be recreated
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Debounced handler to update parent component — stable across renders
  const debouncedOnChange = useMemo(() =>
    createDebouncedFunction((newValue: string) => {
      // Skip during tab switching
      if (isTabSwitching()) return;

      // Update the ref to track what we've sent to parent
      lastPropValueRef.current = newValue;
      onChangeRef.current(newValue);
    }, 50),
    [] // stable — never recreated
  );

  // Handle input change
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Skip during tab switching
    if (isTabSwitching()) return;

    let newValue = uppercase ? e.target.value.toUpperCase() : e.target.value;
    if (numericOnly) {
      newValue = newValue.replace(/\D/g, '');
    }
    if (typeof maxLength === 'number') {
      newValue = newValue.slice(0, Math.max(0, maxLength));
    }
    setLocalValue(newValue);
    debouncedOnChange(newValue);
  }, [debouncedOnChange, uppercase, numericOnly, maxLength]);

  // Handle focus events
  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = false;
    // Ensure the latest value is sent when the field loses focus
    debouncedOnChange.flush();
    if (onBlur) onBlur();
  }, [debouncedOnChange, onBlur]);

  // Update local value when prop value changes - but only if not focused
  React.useEffect(() => {
    // Only update if:
    // 1. The prop value is different from our local value
    // 2. The input is not currently focused (user not typing)
    // 3. The prop value is different from what we last sent (avoid feedback loop)
    if (value !== localValue && !isFocusedRef.current && value !== lastPropValueRef.current) {
      setLocalValue(value || "");
      lastPropValueRef.current = value || "";
    }
  }, [value]);

  // Listen for force update events
  React.useEffect(() => {
    const handleForceUpdate = (event: CustomEvent) => {
      const targetFields = event.detail?.fields || [];
      if (targetFields.includes(name) || targetFields.some((field: string) => name?.includes(field))) {
        setLocalValue(value || "");
        lastPropValueRef.current = value || "";
      }
    };

    window.addEventListener('forceInputUpdate', handleForceUpdate as EventListener);
    return () => {
      window.removeEventListener('forceInputUpdate', handleForceUpdate as EventListener);
    };
  }, [name, value]);

  return (
    <JarvisTextInput
      name={name}
      required={required}
      placeholder={placeholder}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      value={localValue}
      error={error}
      disabled={disabled}
      styles={styles}
      size={size}
      inputMode={inputMode}
      pattern={pattern}
    />
  );
});

JarvisDebouncedTextInput.displayName = 'JarvisDebouncedTextInput';

// Jarvis-themed debounced textarea
export const JarvisDebouncedTextarea: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  styles?: any;
  name?: string;
  rows?: number;
  size?: string;
}> = React.memo(({
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  required = false,
  disabled = false,
  styles = {},
  name,
  rows = 2,
  size = "sm"
}) => {
  const { colors: themeColors } = useTheme();

  // Local state to track input value
  const [localValue, setLocalValue] = useState(value || "");

  // Use refs to track the latest values without causing re-renders
  const lastPropValueRef = useRef(value);
  const isFocusedRef = useRef(false);
  // Ref for onChange so the debounced function never needs to be recreated
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Debounced handler to update parent component — stable across renders
  const debouncedOnChange = useMemo(() =>
    createDebouncedFunction((newValue: string) => {
      // Skip during tab switching
      if (isTabSwitching()) return;

      // Update the ref to track what we've sent to parent
      lastPropValueRef.current = newValue;
      onChangeRef.current(newValue);
    }, 50),
    [] // stable — never recreated
  );

  // Handle input change
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Skip during tab switching
    if (isTabSwitching()) return;

    const newValue = e.target.value.toUpperCase();
    setLocalValue(newValue);
    debouncedOnChange(newValue);
  }, [debouncedOnChange]);

  // Handle focus events
  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
    isFocusedRef.current = false;
    // Ensure the latest value is sent when the field loses focus
    debouncedOnChange.flush();
    if (onBlur) onBlur();
  }, [debouncedOnChange, onBlur]);

  // Update local value when prop value changes - but only if not focused
  React.useEffect(() => {
    // Only update if:
    // 1. The prop value is different from our local value
    // 2. The textarea is not currently focused (user not typing)
    // 3. The prop value is different from what we last sent (avoid feedback loop)
    if (value !== localValue && !isFocusedRef.current && value !== lastPropValueRef.current) {
      setLocalValue(value || "");
      lastPropValueRef.current = value || "";
    }
  }, [value, localValue]);

  // Listen for force update events
  React.useEffect(() => {
    const handleForceUpdate = (event: CustomEvent) => {
      const targetFields = event.detail?.fields || [];
      if (targetFields.includes(name) || targetFields.some((field: string) => name?.includes(field))) {
        setLocalValue(value || "");
        lastPropValueRef.current = value || "";
      }
    };

    window.addEventListener('forceInputUpdate', handleForceUpdate as EventListener);
    return () => {
      window.removeEventListener('forceInputUpdate', handleForceUpdate as EventListener);
    };
  }, [name, value]);

  return (
    <JarvisTextarea
      name={name}
      required={required}
      placeholder={placeholder}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      value={localValue}
      error={error}
      disabled={disabled}
      styles={styles}
      rows={rows}
      size={size}
    />
  );
});

JarvisDebouncedTextarea.displayName = 'JarvisDebouncedTextarea';

// Jarvis-themed debounced currency input
export const JarvisDebouncedCurrencyInput: React.FC<{
  value: string | number;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  styles?: any;
  name?: string;
  size?: string;
}> = React.memo(({
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  required = false,
  disabled = false,
  styles = {},
  name,
  size = "sm"
}) => {
  const { colors: themeColors } = useTheme();

  // Format number with thousand separators
  const formatCurrency = (val: string | number): string => {
    if (!val && val !== 0) return '';
    const numStr = String(val).replace(/[^\d.-]/g, '');
    if (!numStr) return '';

    const parts = numStr.split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.length > 1 ? `${integerPart}.${parts[1]}` : integerPart;
  };

  // Remove formatting to get raw number
  const unformatCurrency = (val: string): string => {
    return val.replace(/,/g, '');
  };

  // Local state to track formatted display value
  const [displayValue, setDisplayValue] = useState(formatCurrency(value));

  // Use refs to track the latest values without causing re-renders
  const lastPropValueRef = useRef(value);
  const isFocusedRef = useRef(false);
  // Ref for onChange so the debounced function never needs to be recreated
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Debounced handler to update parent component — stable across renders
  const debouncedOnChange = useMemo(() =>
    createDebouncedFunction((newValue: string) => {
      // Skip during tab switching
      if (isTabSwitching()) return;

      // Update the ref to track what we've sent to parent
      lastPropValueRef.current = newValue;
      onChangeRef.current(newValue);
    }, 50),
    [] // stable — never recreated
  );

  // Handle input change
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Skip during tab switching
    if (isTabSwitching()) return;

    const inputValue = e.target.value;
    const rawValue = unformatCurrency(inputValue);

    // Allow empty, numbers, decimal point, and negative sign
    if (rawValue === '' || /^-?\d*\.?\d*$/.test(rawValue)) {
      setDisplayValue(formatCurrency(rawValue));
      debouncedOnChange(rawValue);
    }
  }, [debouncedOnChange]);

  // Handle paste events
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    // Skip during tab switching
    if (isTabSwitching()) return;

    const pastedText = e.clipboardData.getData('text');
    const rawValue = unformatCurrency(pastedText);

    // Allow empty, numbers, decimal point, and negative sign
    if (rawValue === '' || /^-?\d*\.?\d*$/.test(rawValue)) {
      setDisplayValue(formatCurrency(rawValue));
      // Update the ref immediately to prevent formatting issues
      lastPropValueRef.current = rawValue;
      // Call onChange immediately without debounce for paste
      onChangeRef.current(rawValue);
    }
  }, []);

  // Handle focus events
  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = false;
    // Ensure the latest value is sent when the field loses focus
    debouncedOnChange.flush();
    if (onBlur) onBlur();
  }, [debouncedOnChange, onBlur]);

  // Update display value when prop value changes - but only if not focused
  React.useEffect(() => {
    if (!isFocusedRef.current && value !== lastPropValueRef.current) {
      setDisplayValue(formatCurrency(value));
      lastPropValueRef.current = value;
    }
  }, [value]);

  // Listen for force update events
  React.useEffect(() => {
    const handleForceUpdate = (event: CustomEvent) => {
      const targetFields = event.detail?.fields || [];
      if (targetFields.includes(name) || targetFields.some((field: string) => name?.includes(field))) {
        setDisplayValue(formatCurrency(value));
        lastPropValueRef.current = value;
      }
    };

    window.addEventListener('forceInputUpdate', handleForceUpdate as EventListener);
    return () => {
      window.removeEventListener('forceInputUpdate', handleForceUpdate as EventListener);
    };
  }, [name, value]);

  return (
    <JarvisTextInput
      name={name}
      required={required}
      placeholder={placeholder}
      onChange={handleChange}
      onPaste={handlePaste}
      onFocus={handleFocus}
      onBlur={handleBlur}
      value={displayValue}
      error={error}
      disabled={disabled}
      styles={styles}
      size={size}
    />
  );
});

JarvisDebouncedCurrencyInput.displayName = 'JarvisDebouncedCurrencyInput';
